import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map } from 'rxjs';

/**
 * Rôles realm Keycloak de l'utilisateur courant.
 *
 * ⚠️ Les rôles realm (`PRESIDENT_CCE`, `CONTROLEUR_ETAT`, …) vivent dans le claim
 * `realm_access.roles` de l'ACCESS token — PAS dans `userData$` (issu du userinfo/ID token).
 * On lit donc l'access token et on décode son payload.
 */
@Injectable({ providedIn: 'root' })
export class RoleService {
  private readonly oidc = inject(OidcSecurityService);

  /** Liste des rôles realm (signal ; vide tant que la session n'est pas restaurée). */
  readonly roles = toSignal(this.oidc.getAccessToken().pipe(map((t) => realmRoles(t))), {
    initialValue: [] as string[],
  });

  readonly isPresident = computed(() => this.roles().includes('PRESIDENT_CCE'));
  readonly isControleur = computed(() => this.roles().includes('CONTROLEUR_ETAT'));

  /**
   * Secrétariat CAEEP — seul rôle porteur de `cac:manage` : c'est lui qui tient le plan de charges des
   * commissaires aux comptes (arbitrage A6, tranché en atelier interne).
   *
   * ⚠️ Sert à **ne pas montrer** une action indisponible, jamais à autoriser : l'autorité reste l'API,
   * qui répond 404 hors portée et 422 sur un refus métier.
   */
  readonly isSecretariatCaeep = computed(() => this.roles().includes('SECRETARIAT_CAEEP'));

  /** Membre du Conseil du CCE — en LECTURE depuis la refonte de l'espace (CCE, 2026-10-05). */
  readonly isConseil = computed(() => this.roles().includes('CONSEIL_CCE'));

  /** Secrétariat du Conseil (كتابة مجلس هيئة مراقبي الدولة — à ratifier) : tient les séances (CCE, 2026-10-05). */
  readonly isSecretariatConseil = computed(() => this.roles().includes('SECRETARIAT_CONSEIL'));

  /**
   * Qui TIENT les séances du Conseil — crée, convoque, saisit l'ordre du jour, les décisions et le PV : le secrétariat
   * du Conseil, et le Président, qui garde ces droits (CCE, 2026-10-05). Les membres lisent.
   * ⚠️ Sert à ne pas montrer une action indisponible ; l'autorité reste l'API.
   */
  readonly tientLeConseil = computed(() => this.isSecretariatConseil() || this.isPresident());

  /**
   * Cellule Administrative et Cellule de Reporting.
   *
   * <p>Ajoutés pour le sélecteur d'espaces : le Président porte désormais les rôles des espaces qu'il
   * peut visiter (Conseil, Administratif, Reporting), et le sélecteur n'affiche qu'un espace dont le
   * rôle est effectivement présent dans le jeton. Le filtrage n'autorise rien — c'est le
   * {@code roleGuard} de la route, puis l'{@code AccessGuard} du serveur, qui décident.
   */
  readonly isCelluleAdministrative = computed(() => this.roles().includes('CELLULE_ADMINISTRATIVE'));
  readonly isCelluleReporting = computed(() => this.roles().includes('CELLULE_REPORTING'));
}

/** Décode `realm_access.roles` du payload d'un access token JWT (robuste, jamais d'exception). */
export function realmRoles(token: string | null | undefined): string[] {
  if (!token) {
    return [];
  }
  try {
    const part = token.split('.')[1];
    if (!part) {
      return [];
    }
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const roles = payload?.realm_access?.roles;
    return Array.isArray(roles) ? (roles as string[]) : [];
  } catch {
    return [];
  }
}
